import {
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, Typography,
    withStyles,
    Button,
    Box,
    Tooltip,
    CircularProgress
} from "@material-ui/core";
import { Cancel, CheckCircle, Edit } from "@material-ui/icons";
import UndoIcon from '@material-ui/icons/Undo';
import RedoIcon from '@material-ui/icons/Redo';
import { NoMeetingRoom, MeetingRoom, VisibilityOff, Visibility, LibraryAddCheck, HighlightOff } from '@material-ui/icons';
import { makeStyles } from "@material-ui/core/styles";
import { useCallback, useEffect, useState } from 'react';
import GetStarted from "./GetStarted";

const StickyTableCell = withStyles((theme) => ({
    head: {
        color: theme.palette.common.white,
        left: 0,
        position: "sticky",
        zIndex: theme.zIndex.appBar + 2
    },
    body: {
        backgroundColor: theme.palette.secondary.main,
        color: "#fff",
        minWidth: "200px",
        left: 0,
        position: "sticky",
        zIndex: theme.zIndex.appBar + 1,
        border: '2px solid #fff',
        padding: 0, borderLeft: 0, borderTop: 0,
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
    }
}))(TableCell);

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        width: 'calc(100vw - 470px)',
        borderRadius: 4,
        maxHeight: 'calc(100vh - 320px)',
        minWidth: '800px',
        minHeight: '400px',
        maxWidth: '100%',
        margin: 'auto',
        '& .MuiTableCell-root': {
            padding: 0,
            border: `1px solid #fff`,
            minWidth: '200px',
            textAlign: 'center'
        },
    },
    topHeading: {
        display: 'flex',
        margin: theme.spacing(0.5, 0, 2, 0),
        alignItems: 'center',
    },
    buttonWrapper: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
    },
    tableSplitHeader: {
        height: '72px',
        maxHeight: '100%',
        color: "#fff",
        background: `linear-gradient(15deg,${theme.palette.secondary.main} 49%, ${theme.palette.primary.main} 50%)`
    },
    activityHeaderCell: {
        backgroundColor: theme.palette.primary.main,
        color: "#fff"
    },
    cellHeaderText: {
        padding: '8px 0'
    }
}));

/*
 * this will build our initialState
 * {
 *      branches: {
 *          "example-branch-objectid": {
 *              name: "branchName",
 *              visibility: true,
 *              activityIds: ["activity-id-1","activity-id-2"]
 *          },
 *          .
 *          .
 *      },
 *      activities: {
 *          "example-activity-id": {
 *              name: "activityName",
 *              visibility: false,
 *              branchIds: ["branch-id-1" "branch-id-2"]
 *          }
 *      },
 *      actions: []
 * }
 */
function computeInitialState(ACTIVITIES, BRANCHES, CONNECTIONS) {
    const DATA = { branches: {}, activities: {}, actions: [] };
    BRANCHES.forEach(b => {
        DATA.branches[b.branch_id] = {
            name: b.branch_name,
            visibility: b.visibility,
            activityIds: []
        }
    });
    ACTIVITIES.forEach(a => {
        DATA.activities[a.activity_template_id] = {
            name: a.activity_name,
            visibility: a.visibility,
            branchIds: []
        }
    });
    console.log("DATA", DATA)
    CONNECTIONS.forEach(connection => {
        let items = DATA.branches[connection.branch_id].activityIds;
        if (items === null) {
            items = [...connection.activity_ids];
        } else {
            items = items.concat(connection.activity_ids);
        }
        DATA.branches[connection.branch_id].activityIds = items;

        connection.activity_ids.forEach(id => {
            DATA.activities[id].branchIds.push(connection.branch_id)
        })
    })
    console.log("CONNECTION", CONNECTIONS)
    console.log("DATA", DATA)
    return DATA;
}

export default function ActivityBranchMatrix({
    activities, editable, handleCancel,
    onSave, toggleEdit, connections, branches
}) {
    const classes = useStyles();
    const initialState = computeInitialState(activities, branches, connections);
    const [state, setState] = useState(initialState);
    const [loading, setLoading] = useState(false);
    const [undo, setUndo] = useState([]);
    const [redo, setRedo] = useState([]);

    const allActivities = activities.map(a => a.activity_template_id);

    const updateState = (newState) => {
        console.log(state, newState)
        setUndo(u => [...u, state]);
        setRedo([]);
        setState(newState);
    }

    const undoChanges = useCallback(() => {
        if (undo.length < 1) {
            return;
        }
        const copyOfUndo = [...undo];
        const lastElement = copyOfUndo.pop();
        console.log(lastElement)
        setUndo(copyOfUndo);
        setRedo(r => [...r, state]);
        setState(lastElement);
    }, [undo, state]);

    const redoChanges = useCallback(() => {
        if (redo.length === 0)
            return;
        const copyOfRedo = [...redo];
        const lastElement = copyOfRedo.pop();
        setRedo(copyOfRedo);
        setUndo(u => [...u, state]);
        setState(lastElement);
    }, [redo, state])

    const handleUserKeyPress = useCallback(event => {
        const { key, ctrlKey } = event;
        if (ctrlKey && key === 'z') {
            undoChanges()
        } else if (ctrlKey && key === 'y') {
            redoChanges();
        }
    }, [undoChanges, redoChanges]);

    useEffect(() => {
        if (editable) {
            window.addEventListener('keydown', handleUserKeyPress);
            return () => {
                window.removeEventListener('keydown', handleUserKeyPress);
            };
        }
    }, [handleUserKeyPress, editable]);

    const addConnection = (branchId, activityIds) => async () => {
        if (!editable)
            return;
        console.log("ADD", branchId, activityIds)
        const copy = JSON.parse(JSON.stringify(state));
        copy.branches[branchId].activityIds = copy.branches[branchId].activityIds.concat(activityIds);
        copy.actions.push({
            action: "ADD",
            branchId,
            activityIds
        })
        console.log("UPDATED", copy)
        updateState(copy);
    }
    const removeConnection = (branchId, activityIds) => async () => {
        if (!editable)
            return;
        console.log("REMOVE", branchId, activityIds)
        const copy = JSON.parse(JSON.stringify(state));
        copy.branches[branchId].activityIds = copy.branches[branchId].activityIds.filter(a => !activityIds.includes(a));
        copy.actions.push({
            action: "REMOVE",
            branchId,
            activityIds
        })
        updateState(copy)
    }
    const addAll = (activityId) => async () => {
        if (!editable)
            return;
        const copy = JSON.parse(JSON.stringify(state));
        Object.keys(copy.branches).forEach(branchId => {
            copy.branches[branchId].activityIds.push(activityId)
        })
        copy.actions.push({
            action: "ADD_TO_ALL",
            branchId: '',
            activityIds: [activityId]
        })
        updateState(copy)
    }
    const removeAll = (activityId) => async () => {
        if (!editable)
            return;
        const copy = JSON.parse(JSON.stringify(state));
        Object.keys(copy.branches).forEach(branchId => {
            copy.branches[branchId].activityIds = copy.branches[branchId].activityIds.filter(a => a !== activityId)
        })
        // state.activities[activityId].isAllSelected = false;
        copy.actions.push({
            action: "REMOVE_FROM_ALL",
            branchId: '',
            activityIds: [activityId]
        })
        updateState(copy)
    }
    const handleBranchVisibility = (branch) => () => {
        const copy = JSON.parse(JSON.stringify(state));
        copy.branches[branch.branch_id].visibility = !copy.branches[branch.branch_id].visibility;
        copy.actions.push({
            action: "TOGGLE_BRANCH_VISIBILITY",
            branchId: branch.branch_id,
            activityIds: ['']
        })
        updateState(copy)
    }
    const handleActivityVisibility = (activity) => () => {
        const copy = JSON.parse(JSON.stringify(state));
        copy.activities[activity.activity_template_id].visibility = !copy.activities[activity.activity_template_id].visibility;
        copy.actions.push({
            action: "TOGGLE_ACTIVITY_VISIBILITY",
            branchId: '',
            activityIds: [activity.activity_template_id]
        })
        updateState(copy)
    }

    const preCancel = () => {
        updateState(computeInitialState(activities, branches, connections));
        setRedo([]);
        setUndo([]);
        handleCancel();
    }

    const handleSave = () => {
        setLoading(true);
        setRedo([]);
        setUndo([]);
        onSave(state.actions).then(() => setLoading(false));
    }


    if (activities.length === 0 || branches.length === 0) {
        return (
            <GetStarted activityCount={activities.length} branchCount={branches.length} />
            // <Typography variant="h5">No Activities/Branches available to show</Typography>
        );
    }

    const ActivityHeaderCell = ({ activity }) => {
        const visibility = state.activities[activity.activity_template_id].visibility;
        return (
            <TableCell key={activity.activity_template_id} className={classes.activityHeaderCell}>
                <div className={classes.cellHeaderText}>{activity.activity_name}</div>
                {editable && <div className={classes.buttonWrapper}>
                    <Tooltip
                        title={visibility ? 'Mark Activity Hidden' : 'Mark Activity Visible'
                        }>
                        <Button
                            color="inherit" size="small"
                            onClick={handleActivityVisibility(activity)}
                        >
                            {!visibility ? <VisibilityOff /> : <Visibility />}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Select all Activities">
                        <Button
                            color="inherit" size="small"
                            onClick={addAll(activity.activity_template_id)}
                            disabled={!visibility}>
                            <LibraryAddCheck />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Uncheck all Activities">
                        <Button
                            color="inherit" size="small"
                            onClick={removeAll(activity.activity_template_id)}
                            disabled={!visibility}>
                            <HighlightOff />
                        </Button>
                    </Tooltip>
                </div>}
            </TableCell>
        )
    }

    const ActivityCell = ({ branch, activity }) => {
        const checked = state.branches[branch.branch_id].activityIds.includes(activity.activity_template_id);
        const isBlocked = (!state.activities[activity.activity_template_id].visibility) || !state.branches[branch.branch_id].visibility;
        return (
            <TableCell
                key={branch.branch_id + activity.activity_template_id}
                style={{ background: isBlocked ? '#ddd' : '#fbfbfb' }}
            >
                <Checkbox
                    checked={checked}
                    style={{ color: !isBlocked ? 'green' : 'gray' }}
                    onClick={
                        checked ? removeConnection(branch.branch_id, [activity.activity_template_id]) : addConnection(branch.branch_id, [activity.activity_template_id])
                    }
                    disabled={isBlocked}
                />
            </TableCell>
        );
    }

    const BranchRow = ({ branch }) => {
        const visibility = state.branches[branch.branch_id].visibility;
        return (
            <TableRow>
                <StickyTableCell>
                    <div className={classes.cellHeaderText}>{branch.branch_name}</div>
                    {editable && <div className={classes.buttonWrapper}>
                        <Tooltip
                            title={!visibility ? 'Mark Branch Open' : 'Mark Branch closed'
                            }>
                            <Button
                                onClick={handleBranchVisibility(branch)}
                                size="small" color="inherit" disableElevation>
                                {!visibility ? <NoMeetingRoom /> : <MeetingRoom />}
                            </Button>
                        </Tooltip>
                        <Tooltip title="Select all Branches">
                            <Button
                                onClick={addConnection(branch.branch_id, allActivities)}
                                disabled={!visibility}
                                color="inherit" size="small" disableElevation>
                                <LibraryAddCheck />
                            </Button>
                        </Tooltip>
                        <Tooltip title="Uncheck all Branches">
                            <Button
                                onClick={removeConnection(branch.branch_id, allActivities)}
                                disabled={!visibility}
                                color="inherit" size="small" disableElevation>
                                <HighlightOff />
                            </Button>
                        </Tooltip>
                    </div>}
                </StickyTableCell>
                {activities.map(activity => <ActivityCell key={branch.branch_id + activity.activity_template_id} branch={branch} activity={activity} />)}
            </TableRow>
        );
    }


    return (
        <>
            <Box className={classes.topHeading}>
                <Typography variant="h4" style={{ 'flex': 1 }}>
                    Overview
                </Typography>
                {
                    editable ? (
                        <>
                            <Button onClick={undoChanges}
                                startIcon={<UndoIcon />}
                                disabled={loading || undo.length === 0}>
                                undo
                            </Button>
                            <Button onClick={redoChanges}
                                startIcon={<RedoIcon />}
                                disabled={loading || redo.length === 0}>
                                redo
                            </Button>
                            <Button onClick={handleSave}
                                style={{ color: 'green' }}
                                disabled={loading}
                                startIcon={
                                    !loading ?
                                        <CheckCircle /> :
                                        <CircularProgress color="inherit" className={classes.circularProgress} size={20} />
                                }
                                disableElevation>
                                apply
                            </Button>
                            <Button style={{ marginRight: 8 }}
                                color="secondary" onClick={preCancel}
                                disabled={loading}
                                startIcon={<Cancel />} disableElevation>
                                cancel
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={toggleEdit}
                            startIcon={<Edit />}
                            color="secondary"
                            variant="contained" disableElevation>
                            Edit
                        </Button>

                    )
                }
            </Box>
            <TableContainer className={classes.tableContainer}>
                <Table stickyHeader>
                    <TableHead className={classes.tableHeader}>
                        <TableRow>
                            <StickyTableCell className={classes.tableSplitHeader}>
                                <div style={{ position: 'absolute', top: 8, right: 8 }}>Activities</div>
                                <div style={{ position: 'absolute', bottom: 8, left: 8 }}>Branches</div>
                            </StickyTableCell>
                            {activities.map(activity => <ActivityHeaderCell key={activity.activity_template_id} activity={activity} />)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {branches.map((d) => (<BranchRow branch={d} key={d.branch_id} />))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )

}